"use client";
import React, { useEffect, useState } from "react";
import Image from "next/image";
import { toWebp } from "@/utils/common";

interface AccordionProps {
  data: any;
}

const RequestProposalBusiness: React.FC<AccordionProps> = ({ data }) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024); 
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [points1, setPoints1] = useState<any[]>([]);
  const [points2, setPoints2] = useState<any[]>([]);
  const [isOpenPremier, setIsOpenPremier] = useState(true);
  const [isOpenStandard, setIsOpenStandard] = useState(true);
  const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL ?? "";

  const bgColors = ["bg-white", "bg-[#E6F3FD]", "bg-white", "bg-[#F3F8E6]"];
  useEffect(() => {
    const p1: any = [];
    const p2: any = [];
    data?.table_rows?.forEach((row: any) => {
      if (row?.points[0]) p1.push(row?.points[0]);
      if (row?.points[1]) p2.push(row?.points[1]);
    });
    setPoints1(p1);
    setPoints2(p2);
  }, [data]);
  const getBgColor = (i: number) => {
    const index = i % bgColors.length;
    return bgColors[index];
  };

  return (
    <section className="bg-white xl:py-[90px] md:py-[80px] py-[56px]">
      <div className="container">
        {/* Title */}
        <div className="heading mb-[40px] xl:w-[70%] lg:w-[80%] mx-auto">
          <h2 className="text-center text-heading-1 font-bold text-primary-text mb-[16px]">
            {data.title}
          </h2>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-[30%_40%_30%] xl:grid-cols-3">
          {/* Column 1 - Capability */}
          <div className="hidden lg:block bg-[#E6F3FD] sm:bg-transparent p-[16px_16px] lg:p-[16px_0px] xl:p-[12px_0_0_0] overflow-hidden border border-blue-100">
            <div className="bg-white p-[24px_24px] xl:p-[39px_24px] flex flex-col justify-center min-h-[154px]">
              <h6 className="text-[20px] xl:text-[24px] font-[600] text-[#040405] leading-[26px] xl:leading-[31.2px] tracking-[1px]">
                {data?.table_rows?.[0]?.title ?? "Capability"}
              </h6>
            </div>
            <div className="content">
              {data?.table_rows?.map(
                (row: any, i: number) =>
                  !!row.title &&
                  i !== 0 && (
                    <div
                      key={`capability-${i}`}
                      className={`p-[10px_24px] flex flex-col justify-center ${getBgColor(i)} min-h-[133px]`}
                    >
                      <p className="text-[18px] md:text-[20px] font-[600] text-[#6B6D75] leading-[25.2px] md:leading-[28px] tracking-[1px]">
                        {row.title}
                      </p>
                    </div>
                  )
              )}
            </div>
          </div>

          {/* Column 2 - Premier Advantage */}
          <div className="bg-[linear-gradient(180deg,rgba(0,134,230,0.6)_0%,rgba(138,188,0,0.6)_100%),url('/images/bg-right.png')] bg-no-repeat bg-right bg-contain p-[16px_10px] xl:p-[12px_0px_12px_12px] border-r border-blue-100">
            <div className="bg-white p-[10px] flex flex-col justify-center min-h-[154px] text-center relative">
              <Image
                src={
                  points1?.[0]?.image?.data?.attributes?.url
                    ? toWebp(baseUrl + points1?.[0]?.image.data.attributes.url)
                    : "/images/about/clock.png"
                }
                alt="Premier"
                width={31}
                height={31}
                className="max-w-[133px] h-auto mx-auto mb-[20px]"
              />
              <div className="relative flex justify-between items-center">
                <h6 className="w-full text-[20px] xl:text-[24px] p-[2px_18px] md:p-0 font-[600] text-[#040405] leading-[26px] xl:leading-[31.2px] tracking-[1px]">
                  {points1?.[0]?.title || "Premier Advantage"}
                </h6>


                {isMobile && (
                  <Image
                    src="/images/r-down-icon.svg"
                    alt="Toggle Premier"
                    width={20}
                    height={20}
                    onClick={() => setIsOpenPremier(!isOpenPremier)}
                    className={`absolute right-0 top-[30%] translate-y-[-50%] cursor-pointer transition-transform duration-300 ${isOpenPremier ? "rotate-0" : "-rotate-90"
                      }`}
                  />
                )}
              </div>
            </div>
            {(isMobile ? isOpenPremier : true) && (
              <div className="content">
                {data?.table_rows?.map(
                  (item: any, i: number) =>
                    i !== 0 && (
                      <div
                        key={`premier-${i}`}
                        className={`p-[10px] lg:p-[10px_12px] xl:p-[10px_12px] 2xl:p-[10px_35px] flex flex-col justify-center ${getBgColor(i)} min-h-[133px]`}
                      >
                        {isMobile && (
                          <p className="block lg:hidden mb-[10px] text-[18px] md:text-[20px] xl:text-[24px] text-[#6B6D75] font-[400] leading-[26px] md:leading-[28px] xl:leading-[31.2px] tracking-[1px] text-center">
                            {item?.title}
                          </p>
                        )}
                        <p className="text-[18px] md:text-[20px] xl:text-[24px] font-[400] text-[#040405] leading-[26px] md:leading-[28px] xl:leading-[31.2px] tracking-[1px] text-center">
                          {item?.points[0]?.title ?? ""}
                        </p>
                      </div>
                    )
                )}
              </div>
            )}
          </div>

          {/* Column 3 - Standard Offering */}
          <div className="bg-[linear-gradient(180deg,rgba(0,134,230,0.6)_0%,rgba(138,188,0,0.6)_100%),url('/images/bg-right.png')] bg-no-repeat bg-right bg-contain p-[16px_10px] xl:p-[12px_12px_12px_0]">
            <div className="bg-white p-[24px_10px] xl:p-[25px_10px] flex flex-col justify-center min-h-[154px] text-center relative">
              <Image
                src={
                  points2?.[0]?.image?.data?.attributes?.url
                    ? toWebp(baseUrl + points2?.[0]?.image.data.attributes.url)
                    : "/images/about/clock.png"
                }
                alt="Standard"
                width={31}
                height={31}
                quality={100}
                className="max-w-[133px] h-auto mx-auto mb-[20px]"
              />
              <div className="relative flex justify-between items-center">
                <h6 className="w-full text-[20px] xl:text-[24px] p-[2px_18px] md:p-0 font-[600] text-[#040405] leading-[26px] xl:leading-[31.2px] tracking-[1px]">
                  {points2?.[0]?.title || "Standard Industry Offering"}
                </h6>

                {isMobile && (
                  <Image
                    src="/images/r-down-icon.svg"
                    alt="Toggle Standard"
                    width={20}
                    height={20}
                    onClick={() => setIsOpenStandard(!isOpenStandard)}
                    className={`absolute right-0 top-[30%] translate-y-[-50%] cursor-pointer transition-transform duration-300 ${isOpenStandard ? "rotate-0" : "-rotate-90"
                      }`}
                  />
                )}
              </div>
            </div>

            {(isMobile ? isOpenStandard : true) && (
              <div className="content">
                {data?.table_rows?.map(
                  (item: any, i: number) =>
                    i !== 0 && (
                      <div
                        key={`standard-${i}`}
                        className={`p-[10px] flex flex-col justify-center ${getBgColor(i)} min-h-[133px]`}
                      >
                        {isMobile && (
                          <p className="block lg:hidden mb-[10px] text-[18px] md:text-[20px] text-[#6B6D75] font-[600] leading-[28px] md:leading-[26px] tracking-[1px] text-center">
                            {item?.title ?? ""}
                          </p>
                        )}
                        <p className="text-[18px] md:text-[20px] font-[600] text-[#6B6D75] leading-[25.2px] md:leading-[28px] tracking-[1px] text-center">
                          {item?.points[1]?.title ?? ""}
                        </p>
                      </div>
                    )
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default RequestProposalBusiness;
